/**
 * OpenAI Service for AI Chat Loop
 */

import { app } from "@/lib/app"

const OPENAI_API_BASE = "https://api.openai.com/v1"

interface ChatMessage {
  role: "system" | "user" | "assistant"
  content: string
}

class OpenAIService {
  private apiKey: string | null = null

  async initialize(teamId: string): Promise<void> {
    const apiKeys = await app.fetchAPIkeys(teamId)
    if (!apiKeys.openai) {
      throw new Error("OpenAI API key not found")
    }
    this.apiKey = apiKeys.openai
  }

  async getChatCompletion(
    messages: ChatMessage[],
    model: string = "gpt-4.1-mini",
    temperature: number = 0.7
  ): Promise<string> {
    if (!this.apiKey) {
      throw new Error("OpenAI service not initialized")
    }

    const modelName = model.includes("/") ? model.split("/").pop()! : model

    const response = await fetch(`${OPENAI_API_BASE}/chat/completions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model: modelName,
        messages,
        temperature,
        max_tokens: 2000,
      }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error?.message || "OpenAI API request failed")
    }

    const data = await response.json()
    return data.choices[0]?.message?.content || ""
  }

  async generateNextMessage(
    systemPrompt: string,
    conversationHistory: Array<{ role: "user" | "assistant"; content: string }>,
    model: string = "gpt-4.1-mini"
  ): Promise<string> {
    if (!this.apiKey) {
      throw new Error("OpenAI service not initialized")
    }

    // Strip provider prefix (e.g. "openai/gpt-4.1-mini" -> "gpt-4.1-mini")
    const modelName = model.includes("/") ? model.split("/").pop()! : model

    // Swap roles: the LLM is generating the "user" side of the conversation.
    // From the LLM's perspective, ITS previous messages are "assistant" (what it said before)
    // and the chatbot agent's messages are "user" (what it's responding to).
    const swappedHistory: ChatMessage[] = conversationHistory.map(m => ({
      role: m.role === "user" ? "assistant" as const : "user" as const,
      content: m.content,
    }))

    const enhancedSystemPrompt = `${systemPrompt}

IMPORTANT: You are playing the role of a PERSON who is chatting with a helpline bot. The "user" messages below are from the BOT/AGENT. The "assistant" messages are what YOU (the simulated person) said previously. Generate your next message as this person. Keep it natural, short (1-2 sentences), and in the same language the conversation is in. Do NOT repeat yourself or ask the same question again. Progress the conversation forward based on the bot's latest response.`

    const messages: ChatMessage[] = [
      { role: "system", content: enhancedSystemPrompt },
      ...swappedHistory,
    ]

    const response = await fetch(`${OPENAI_API_BASE}/chat/completions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model: modelName,
        messages,
        temperature: 0.7,
        max_tokens: 500,
      }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error?.message || "OpenAI API request failed")
    }

    const data = await response.json()
    return data.choices[0]?.message?.content || ""
  }
}

export const openaiService = new OpenAIService()
export { OpenAIService }
